import { useRef } from "react";
import {
  Upload,
  Move,
  RotateCcw,
  RotateCw,
  Plus,
  Minus,
  Save,
  Undo2,
  Trash2,
  Sparkles,
  Loader2,
  SlidersHorizontal,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Map as MapIcon,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STEPS = [1, 5, 10, 25, 50];

// Full PDF-plan management, rendered at the top of the settings screen: upload,
// AI alignment, manual positioning, opacity and delete.
export default function OverlayManager({ ov }) {
  const fileRef = useRef(null);
  const {
    overlay,
    opacity,
    setOpacity,
    editMode,
    setEditMode,
    step,
    setStep,
    dirty,
    loading,
    uploading,
    uploadPct,
    saving,
    aiBusy,
    askAlign,
    setAskAlign,
    busy,
    runAuto,
    onFile,
    apply,
    doSave,
    resetToSaved,
    persistProps,
    doDelete,
  } = ov;

  const StepBtn = ({ onClick, testid, title, children }) => (
    <button
      onClick={onClick}
      data-testid={testid}
      title={title}
      className="grid h-9 w-9 place-items-center rounded-xl bg-white text-slate-700 shadow ring-1 ring-slate-200 transition-transform hover:scale-105 active:scale-95"
    >
      {children}
    </button>
  );

  return (
    <div className="space-y-2 border-b border-slate-100 pb-3" data-testid="settings-overlay-manager">
      <p className="flex items-center gap-2 text-xs text-slate-500">
        <MapIcon size={14} className="text-slate-400" /> Kartenplan (PDF)
      </p>

      <input
        ref={fileRef}
        type="file"
        accept="application/pdf,.pdf,image/*"
        onChange={onFile}
        className="hidden"
        data-testid="overlay-file-input"
      />

      <AlertDialog open={askAlign} onOpenChange={setAskAlign}>
        <AlertDialogContent data-testid="overlay-align-dialog" className="z-[1000] max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Jetzt automatisch ausrichten?</AlertDialogTitle>
            <AlertDialogDescription>
              Die KI versucht, den Lageplan passend über die Karte zu legen. Du kannst die
              Position danach jederzeit manuell feinjustieren.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="overlay-align-later">Nein, später</AlertDialogCancel>
            <AlertDialogAction
              data-testid="overlay-align-now"
              onClick={() => {
                if (overlay) runAuto(overlay.id);
              }}
            >
              Ja, automatisch ausrichten
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {loading ? (
        <div className="grid place-items-center py-4">
          <Loader2 className="animate-spin text-slate-400" size={20} />
        </div>
      ) : !overlay ? (
        <>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            data-testid="overlay-load-pdf"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-2.5 text-sm font-bold text-white shadow transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
          >
            {busy ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
            {uploading ? `Lädt… ${uploadPct}%` : aiBusy ? "KI richtet aus…" : "Overlay aus PDF laden"}
          </button>
          {uploading && (
            <div data-testid="overlay-upload-progress">
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full rounded-full bg-slate-900 transition-[width] duration-200 ease-out"
                  style={{ width: `${uploadPct}%` }}
                  data-testid="overlay-upload-progress-bar"
                />
              </div>
              <p className="mt-1 text-center text-[11px] font-semibold tabular-nums text-slate-500">
                {uploadPct}% hochgeladen
              </p>
            </div>
          )}
          <p className="text-[10px] leading-relaxed text-slate-400">
            Lade einen Lageplan als PDF (oder Bild). Ein-/Ausblenden und die Basiskarte findest du
            im Ebenen-Menü unten links.
          </p>
        </>
      ) : (
        <>
          <p className="truncate text-sm font-semibold text-slate-800" title={overlay.name}>
            {overlay.name || "Overlay"}
          </p>

          <div>
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.05"
              value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
              onPointerUp={() => persistProps({ opacity })}
              data-testid="overlay-opacity"
              className="w-full accent-slate-900"
            />
            <p className="text-[10px] uppercase tracking-widest text-slate-400">
              Deckkraft {Math.round(opacity * 100)}%
            </p>
          </div>

          {!editMode ? (
            <div className="flex flex-col gap-2">
              <button
                onClick={() => runAuto(overlay.id)}
                disabled={busy}
                data-testid="overlay-auto-position"
                className="flex items-center justify-center gap-2 rounded-xl bg-indigo-600 py-2.5 text-sm font-bold text-white shadow transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-50"
              >
                {aiBusy ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
                {aiBusy ? "KI richtet aus…" : "Automatisch ausrichten (KI)"}
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditMode(true)}
                  data-testid="overlay-edit-start"
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-white py-2.5 text-sm font-bold text-slate-700 shadow ring-1 ring-slate-200 transition-transform hover:scale-[1.02] active:scale-95"
                >
                  <Move size={16} /> Verschieben
                </button>
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={busy}
                  data-testid="overlay-replace"
                  title="Anderes PDF laden"
                  className="grid h-[42px] w-11 place-items-center rounded-xl bg-white text-slate-700 shadow ring-1 ring-slate-200 disabled:opacity-50"
                >
                  <Upload size={16} />
                </button>
                <button
                  onClick={doDelete}
                  data-testid="overlay-delete"
                  title="Overlay entfernen"
                  className="grid h-[42px] w-11 place-items-center rounded-xl bg-white text-red-600 shadow ring-1 ring-slate-200"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ) : (
            <div data-testid="overlay-edit-panel">
              <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">
                <SlidersHorizontal size={13} /> Verschieben
                {dirty && (
                  <span className="ml-auto text-amber-600" data-testid="overlay-dirty">
                    • ungespeichert
                  </span>
                )}
              </div>

              <div className="mb-2 flex gap-1.5">
                {STEPS.map((v) => (
                  <button
                    key={v}
                    onClick={() => setStep(v)}
                    data-testid={`overlay-step-${v}`}
                    className={`flex-1 rounded-lg py-1.5 text-xs font-bold transition-colors ${
                      step === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
              <p className="mb-2 text-[10px] text-slate-400">
                Bewegen {step} m · Drehen {step}° · Größe {step}%
              </p>

              <div className="flex items-start justify-between gap-2">
                <div className="grid grid-cols-3 gap-1.5">
                  <div />
                  <StepBtn onClick={() => apply({ dN: step })} testid="overlay-move-n" title="Norden">
                    <ArrowUp size={17} />
                  </StepBtn>
                  <div />
                  <StepBtn onClick={() => apply({ dE: -step })} testid="overlay-move-w" title="Westen">
                    <ArrowLeft size={17} />
                  </StepBtn>
                  <div className="grid h-9 w-9 place-items-center text-slate-300">
                    <Move size={15} />
                  </div>
                  <StepBtn onClick={() => apply({ dE: step })} testid="overlay-move-e" title="Osten">
                    <ArrowRight size={17} />
                  </StepBtn>
                  <div />
                  <StepBtn onClick={() => apply({ dN: -step })} testid="overlay-move-s" title="Süden">
                    <ArrowDown size={17} />
                  </StepBtn>
                  <div />
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className="flex gap-1.5">
                    <StepBtn onClick={() => apply({ rotDeg: step })} testid="overlay-rotate-ccw" title="Drehen">
                      <RotateCcw size={17} />
                    </StepBtn>
                    <StepBtn onClick={() => apply({ rotDeg: -step })} testid="overlay-rotate-cw" title="Drehen">
                      <RotateCw size={17} />
                    </StepBtn>
                  </div>
                  <div className="flex gap-1.5">
                    <StepBtn onClick={() => apply({ scale: 1 + step / 100 })} testid="overlay-scale-plus" title="Größer">
                      <Plus size={17} />
                    </StepBtn>
                    <StepBtn onClick={() => apply({ scale: 1 - step / 100 })} testid="overlay-scale-minus" title="Kleiner">
                      <Minus size={17} />
                    </StepBtn>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <button
                  onClick={doSave}
                  disabled={saving || !dirty}
                  data-testid="overlay-save"
                  className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-slate-900 py-2.5 text-sm font-bold text-white shadow transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-40"
                >
                  {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Speichern
                </button>
                <button
                  onClick={resetToSaved}
                  disabled={!dirty}
                  data-testid="overlay-reset"
                  title="Zurücksetzen"
                  className="grid h-[42px] w-11 place-items-center rounded-xl bg-white text-slate-600 shadow ring-1 ring-slate-200 disabled:opacity-40"
                >
                  <Undo2 size={16} />
                </button>
                <button
                  onClick={() => setEditMode(false)}
                  data-testid="overlay-edit-done"
                  className="rounded-xl bg-white px-3 text-sm font-bold text-slate-600 shadow ring-1 ring-slate-200"
                >
                  Fertig
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
